import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { motion } from 'motion/react';
import { useRouter } from 'next/router';
import { getSearchTerm } from '@/features/SearchPage/utils/searchUrl';
import { useRouterSafe } from '@/hooks/navigation/useRouterSafe';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Ref } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

// Import directly from individual files to avoid circular dependencies
import { getTrendingSearches } from '@/features/GlobalSearch/utils/getTrendingSearches';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { useTracking } from '@/providers/TrackingProvider';
import { trendingKeys } from '@/utils/queryKeys';
import { CACHE_TIMES, OVERLAY_ANIMATION, PLACEHOLDER_TEXTS, RECENT_SEARCH_THRESHOLD_MS } from './constants';
import { GlobalSearchForm } from './globalSearchForm';
import { openGlobalSearchRequestAtom } from './openGlobalSearchRequest';
import { createSearchHandlers } from './searchHandlers';
import { SearchPopup } from './searchPopup';
import { cachedSearchResultsAtom, searchOverlayAtom, searchResultsAtom } from './searchStorage';
import { createDebouncedSearch, createSearchByKeyword, createUpdateSearchCache } from './searchUtils';
import { ASSET_TYPE } from '@/types/asset';
import type { FormValues } from './globalSearchForm';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import { captureException } from '@sentry/nextjs';

// Export the atoms from our storage module
export { cachedSearchResultsAtom, searchOverlayAtom, searchResultsAtom } from './searchStorage';

// The search page only renders on a canonical library slug; anything else is
// redirected or 404s server-side.
const SEARCH_LIBRARY_ASSET_TYPES: Record<string, AssetType> = {
  music: 'music',
  sfx: 'sfx',
  'stock-video': ASSET_TYPE.STOCK_VIDEO,
  'motion-graphics': 'motion',
  luts: 'luts'
};
export interface GlobalSearchHandle {
  openSearch: () => void;
}
interface GlobalSearchProps {
  /** Only the top-bar instance should opt in */
  listenForOpenRequest?: boolean;
  ref?: Ref<GlobalSearchHandle>;
}
export const GlobalSearch = ({
  ref,
  listenForOpenRequest = false
}: GlobalSearchProps) => {
  const router = useRouter();
  const {
    asPath
  } = useRouterSafe();
  // router.pathname (the static route template) is correct even before
  // router.isReady, unlike useRouterSafe's '/' fallback — which made this
  // component render null via isHomePage for the first frames after the
  // dynamic import resolved, collapsing the header and shifting the page.
  const {
    pathname
  } = router;
  const isSearchPage = pathname.startsWith('/search/');
  const queryClient = useQueryClient();
  const {
    isMd
  } = useTWScreens();
  const [isHovered, setIsHovered] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchResults, setSearchResults] = useAtom(searchResultsAtom);
  const [cachedSearchResults, setCachedSearchResults] = useAtom(cachedSearchResultsAtom);
  const [, setSearchOverlay] = useAtom(searchOverlayAtom);
  const [isOpenRequested, setIsOpenRequested] = useAtom(openGlobalSearchRequestAtom);
  const [isSelectOpened, setIsSelectOpened] = useState(false);
  const {
    initializeTracking
  } = useTracking();

  // Update overlay state when search visibility changes
  useEffect(() => {
    setSearchOverlay(isSearchVisible);
  }, [isSearchVisible, setSearchOverlay]);

  // Check if current path is a home page for each section
  const isHomePage = pathname === '/' || pathname === '';
  const isSfxHomePage = pathname === '/sfx' || pathname.endsWith('/sfx');
  const isMotionGraphicsHomePage = pathname === '/motion-graphics';
  const isMusicHomePage = pathname === '/music' || pathname.endsWith('/music');
  const isStockFootageHomePage = pathname === '/stock-video';
  const isLutsHomePage = pathname === '/luts';
  const isTrendingPage = pathname.includes('/trending');

  // Check if URL contains any of the asset types
  const urlContainsAssetType = useMemo(() => {
    const lowerPath = asPath.toLowerCase();
    const assetPatterns = ['motion', 'sfx', 'music', 'stock-video', 'zones', 'browse', 'luts'];
    return assetPatterns.some(pattern => lowerPath.includes(pattern));
  }, [asPath]);

  // Determine if current page should have special styling
  const isSpecialPage = isHomePage || isSfxHomePage || isMotionGraphicsHomePage || isMusicHomePage || isStockFootageHomePage || isLutsHomePage;
  const shouldShowSimpleSearch = isSpecialPage || isTrendingPage || urlContainsAssetType;

  // Determine the current asset type based on the URL path
  const currentAssetType = useMemo((): AssetType => {
    // If on search page, read the type from query parameter
    if (isSearchPage) {
      const library = SEARCH_LIBRARY_ASSET_TYPES[String(router.query.type)];
      if (library) {
        return library;
      }
    }

    // Map pathname patterns to asset types
    const pathPatterns: Array<{
      patterns: string[];
      type: AssetType;
    }> = [{
      patterns: ['/sfx'],
      type: 'sfx'
    }, {
      patterns: ['/luts'],
      type: 'luts'
    }, {
      patterns: ['/motion-graphics'],
      type: 'motion'
    }, {
      patterns: ['/stock-video'],
      type: ASSET_TYPE.STOCK_VIDEO as AssetType
    }];
    const match = pathPatterns.find(({
      patterns
    }) => patterns.some(pattern => pathname.includes(pattern)));
    return match?.type ?? 'music';
  }, [pathname, isSearchPage, router.query.type]);

  // Get initial keyword from URL if on search page, or from search result page
  const initialKeyword = useMemo(() => {
    if (isSearchPage) {
      return getSearchTerm(router.query);
    }

    // Check if we're on a search result page (e.g., redirect page)
    try {
      const lastQuery = sessionStorage.getItem('lastSearchQuery');
      const lastSearchResultUrl = sessionStorage.getItem('lastSearchResultUrl');
      if (lastQuery && pathname === lastSearchResultUrl) {
        return lastQuery;
      }
    } catch {
      // Ignore if sessionStorage is not available
    }
    return '';
  }, [pathname, isSearchPage, router.query]);

  // Initialize the form with the current asset type and keyword from URL
  const methods = useForm<FormValues>({
    defaultValues: {
      keyword: initialKeyword,
      assetType: currentAssetType
    }
  });

  // Get the current asset type from the form
  const activeAssetType = methods.watch('assetType');

  // Get the appropriate placeholder text based on the active asset type and search visibility
  const getPlaceholderText = useMemo(() => {
    if (!isMd) {
      // Always use asset-specific placeholder on mobile
      return PLACEHOLDER_TEXTS[activeAssetType];
    }

    // First check if we're in a page that should show simple search text
    if (shouldShowSimpleSearch) {
      // If search is visible and we have a valid asset type, show the specific placeholder
      if (isSearchVisible && ['music', 'sfx', 'motion', ASSET_TYPE.STOCK_VIDEO, 'luts'].includes(activeAssetType)) {
        return PLACEHOLDER_TEXTS[activeAssetType];
      }

      // Otherwise just show "Search"
      return 'Search';
    }

    // For all other pages, show "Search Uppbeat"
    return 'Search Uppbeat';
  }, [isMd, isSearchVisible, shouldShowSimpleSearch, activeAssetType]);

  // References for search state
  const currentSearchRef = useRef<{
    keyword: string;
    assetType: AssetType;
    requestId: number;
  }>({
    keyword: '',
    assetType: currentAssetType,
    requestId: 0
  });

  // Request ID counter to track the latest request
  const requestIdCounter = useRef(0);

  // Create a debounced cache updater using our extracted utility
  const updateSearchCache = useMemo(() => createUpdateSearchCache(setCachedSearchResults), [setCachedSearchResults]);

  // Create the search function using our extracted utility
  const searchByKeyword = useCallback((keyword: string, assetType: AssetType) => createSearchByKeyword(setSearchResults, cachedSearchResults, updateSearchCache, requestIdCounter, currentSearchRef)(keyword, assetType), [setSearchResults, cachedSearchResults, updateSearchCache]);

  // Create the debounced search function using our extracted utility
  const debouncedSearch = useMemo(() => createDebouncedSearch(searchByKeyword, setSearchResults, cachedSearchResults, requestIdCounter, currentSearchRef), [searchByKeyword, setSearchResults, cachedSearchResults]);

  // Search state management functions
  const {
    openSearch,
    closeSearch
  } = useMemo(() => {
    const open = () => {
      // Set search visible and never close it programmatically —
      // only user action should close the search.
      setIsSearchVisible(true);
      setIsHovered(false);
      const existingKeyword = methods.getValues('keyword');
      const assetType = methods.getValues('assetType');
      currentSearchRef.current = {
        keyword: existingKeyword || '',
        assetType,
        requestId: requestIdCounter.current + 1
      };

      // Always trigger a new search if there's a keyword
      if (existingKeyword?.trim()) {
        // Load from cache first for immediate feedback
        const cachedResult = cachedSearchResults[`${existingKeyword}::${assetType}`];
        if (cachedResult) {
          setSearchResults(cachedResult);
        }

        // Always trigger a fresh search when reopening
        debouncedSearch.immediate(existingKeyword, assetType);
      }
    };
    const close = () => {
      // Don't reset search state when closing — this preserves the search term
      // and results for when we reopen.
      setIsSearchVisible(false);
      setIsSelectOpened(false);
    };
    return {
      openSearch: open,
      closeSearch: close
    };
  }, [methods, debouncedSearch, cachedSearchResults, setSearchResults]);

  // Create input handlers using our extracted utility
  const {
    handleInputChange,
    handleSubmit,
    handleKeyDown,
    handleAssetTypeChange,
    handleInputBlur
  } = useMemo(() => createSearchHandlers(methods, router, setSearchResults, debouncedSearch, cachedSearchResults, isSearchVisible, openSearch, closeSearch, currentSearchRef, requestIdCounter, currentAssetType, initializeTracking), [methods, router, setSearchResults, debouncedSearch, cachedSearchResults, isSearchVisible, openSearch, closeSearch, currentAssetType, initializeTracking]);

  // Update the form's assetType value when currentAssetType changes
  useEffect(() => {
    methods.setValue('assetType', currentAssetType);
  }, [currentAssetType, methods]);

  // Update the form's keyword value when URL query changes
  useEffect(() => {
    // Helper to update keyword only if different
    const updateKeywordIfChanged = (newKeyword: string) => {
      if (newKeyword && methods.getValues('keyword') !== newKeyword) {
        methods.setValue('keyword', newKeyword);
      }
    };
    if (isSearchPage) {
      updateKeywordIfChanged(getSearchTerm(router.query));
      return;
    }

    // Check if we're on a search result page (e.g., redirect page)
    try {
      const lastQuery = sessionStorage.getItem('lastSearchQuery');
      const lastSearchResultUrl = sessionStorage.getItem('lastSearchResultUrl');
      if (lastQuery && pathname === lastSearchResultUrl) {
        updateKeywordIfChanged(lastQuery);
      }
    } catch {
      // Ignore if sessionStorage is not available
    }
  }, [pathname, isSearchPage, router.query, methods]);

  // Create a reusable prefetch function that makes a single multi_search call
  const prefetchTrendingSearches = useCallback(async () => {
    try {
      await queryClient.prefetchQuery({
        queryKey: trendingKeys.searches(),
        queryFn: getTrendingSearches,
        staleTime: CACHE_TIMES.STALE_TIME,
        gcTime: CACHE_TIMES.GC_TIME
      });
    } catch (error) {
      // Silent fail for prefetch
      captureException('Failed to prefetch trending searches', {
        extra: {
          error
        }
      });
    }
  }, [queryClient]);

  // Prefetch trending searches when hovering over search input
  useEffect(() => {
    if (isHovered) {
      prefetchTrendingSearches();
    }
  }, [isHovered, prefetchTrendingSearches]);
  useQuery({
    queryKey: trendingKeys.searches(),
    queryFn: getTrendingSearches,
    staleTime: CACHE_TIMES.STALE_TIME,
    gcTime: CACHE_TIMES.GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    enabled: isSearchVisible
  });

  // Listen for global close search events
  useEffect(() => {
    const handleCloseSearch = () => {
      closeSearch();
    };
    window.addEventListener('closeGlobalSearch', handleCloseSearch);
    return () => {
      window.removeEventListener('closeGlobalSearch', handleCloseSearch);
    };
  }, [closeSearch]);
  useEffect(() => {
    if (!(listenForOpenRequest && isOpenRequested)) {
      return;
    }
    setIsOpenRequested(false);
    openSearch();
    // Mobile drawer autofocuses; desktop needs this
    searchContainerRef.current?.querySelector('input')?.focus();
  }, [listenForOpenRequest, isOpenRequested, setIsOpenRequested, openSearch]);

  // Track where we land after a search (handles redirects) using a ref
  const searchResultPageRef = useRef<string | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    try {
      const lastSearchTimestamp = sessionStorage.getItem('lastSearchTimestamp');
      const currentPath = asPath.split('?')[0];
      if (!lastSearchTimestamp) {
        return;
      }
      const timeSinceSearch = Date.now() - Number.parseInt(lastSearchTimestamp, 10);

      // If we just searched (within threshold), update both storage and ref
      if (timeSinceSearch < RECENT_SEARCH_THRESHOLD_MS) {
        sessionStorage.setItem('lastSearchResultUrl', currentPath);
        searchResultPageRef.current = currentPath;
      }
    } catch {
      // Ignore if sessionStorage is not available
    }
  }, [asPath]);

  // Clear search field when navigating away from search result pages
  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      try {
        const currentPath = asPath.split('?')[0];
        if (!searchResultPageRef.current) {
          return;
        }

        // Check if we're currently on the stored result page
        const isOnResultPage = currentPath === searchResultPageRef.current;
        if (!isOnResultPage) {
          return;
        }

        // Check if we're navigating to another search page
        const newPathname = url.split('?')[0];
        const isNavigatingToSearch = newPathname.includes('/search');

        // Clear if navigating away from result page to a non-search page
        if (!isNavigatingToSearch) {
          methods.setValue('keyword', '');
          setSearchResults(undefined);
          sessionStorage.removeItem('lastSearchResultUrl');
          sessionStorage.removeItem('lastSearchQuery');
          sessionStorage.removeItem('lastSearchTimestamp');
          searchResultPageRef.current = null;
        }
      } catch {
        // Ignore if sessionStorage is not available
      }
    };
    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.events, methods, setSearchResults, asPath]);

  // Expose the openSearch method via ref
  useImperativeHandle(ref, () => ({
    openSearch
  }));
  if (isHomePage) {
    return null;
  }
  return <>
      <div className="relative w-full bg-transparent pt-0" ref={searchContainerRef}>
        <FormProvider {...methods} data-sentry-element="FormProvider" data-sentry-source-file="globalSearch.tsx">
          <div className="pointer-events-auto relative w-full sm:max-w-[380px] lg:max-w-[450px] xl:max-w-[530px] 2xl:max-w-[572px]">
            {/* Desktop search form — gated with CSS rather than `isMd` so the
                first client render is already laid out correctly at every
                viewport (useMedia defaults to true and only corrects in an
                effect, which would flash the form on mobile / a gap on md+). */}
            <div className="hidden md:block">
              <GlobalSearchForm getPlaceholderText={getPlaceholderText} handleAssetTypeChange={handleAssetTypeChange} handleInputBlur={handleInputBlur} handleInputChange={handleInputChange} handleKeyDown={handleKeyDown} handleSubmit={handleSubmit} isHovered={isHovered} isSearchVisible={isSearchVisible} isSelectOpened={isSelectOpened} openSearch={openSearch} setIsHovered={setIsHovered} setIsSelectOpened={setIsSelectOpened} setSearchResults={setSearchResults} data-sentry-element="GlobalSearchForm" data-sentry-source-file="globalSearch.tsx" />
            </div>

            {/* Search popup/drawer */}
            <SearchPopup activeAssetType={activeAssetType} closeSearch={closeSearch} getPlaceholderText={getPlaceholderText} handleAssetTypeChange={handleAssetTypeChange} handleInputBlur={handleInputBlur} handleInputChange={handleInputChange} handleKeyDown={handleKeyDown} handleSubmit={handleSubmit} isHovered={isHovered} isSearchVisible={isSearchVisible} isSelectOpened={isSelectOpened} openSearch={openSearch} searchResults={searchResults} setIsHovered={setIsHovered} setIsSelectOpened={setIsSelectOpened} setSearchResults={setSearchResults} data-sentry-element="SearchPopup" data-sentry-source-file="globalSearch.tsx" />
          </div>
        </FormProvider>
      </div>

      {/* Search overlay - moved from SearchPopup */}
      {!!isSearchVisible && !!isMd && <motion.div animate={{
      opacity: 1
    }} className="fixed top-0 bottom-0 -left-[255px] z-20 h-[100vh] overflow-x-hidden overflow-y-scroll bg-black/20 backdrop-blur-px will-change-[opacity]" exit={{
      opacity: 0
    }} initial={{
      opacity: 0
    }} onClick={closeSearch} style={{
      willChange: 'opacity',
      backfaceVisibility: 'hidden' as const,
      WebkitTransform: 'translate3d(0,0,0)',
      width: 'calc(100vw + 255px)'
    }} transition={{
      duration: OVERLAY_ANIMATION.DURATION_SECONDS,
      ease: OVERLAY_ANIMATION.EASING
    }} />}
    </>;
};